import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Users, ShieldCheck, FileSpreadsheet, MessageSquare, Plus, Trash2, Pencil, Filter as FilterIcon } from "lucide-react";
import { fetchKontakthistorie, addKontakthistorie, deleteKontakthistorie } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const KANAL_ICONS = {
  "Mail": Mail,
  "Telefon": Phone,
  "Treffen": Users,
  "Brief": Mail,
  "System": ShieldCheck,
  "Sonstiges": MessageSquare,
};

const TYP_LABEL = {
  manuell: "Manuell",
  auto_seed_bnp: "BNP-Seed",
  auto_phase_wechsel: "Phase",
  auto_dokument_status: "Dokument",
  auto_sperre: "Sperre",
  auto_datenraum: "Datenraum",
  auto_reminder: "Reminder",
  auto_bulk: "Bulk",
};

const BEARBEITER_OPTS = ["Heinrichs", "Gehring", "Kasper", "Adldinger", "BNP", "System"];
const KANAL_OPTS = ["Mail", "Telefon", "Treffen", "Brief", "Sonstiges"];

export default function KontakthistorieTab({ investorId, onChange }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all|manuell|auto
  const [adding, setAdding] = useState(false);
  // New-entry form
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [bearbeiter, setBearbeiter] = useState("Heinrichs");
  const [kanal, setKanal] = useState("Mail");
  const [text, setText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchKontakthistorie(investorId);
      setData(d);
    } catch { toast.error("Historie konnte nicht geladen werden"); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (investorId) load(); }, [investorId]);

  const handleAdd = async () => {
    if (!text.trim()) { toast.error("Bitte Text eingeben"); return; }
    setAdding(true);
    try {
      await addKontakthistorie(investorId, { datum, bearbeiter, kanal, text });
      toast.success("Eintrag hinzugefügt.");
      setText("");
      load();
      onChange?.();
    } catch { toast.error("Anlegen fehlgeschlagen"); }
    finally { setAdding(false); }
  };

  const handleDelete = async (eintragId) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    try {
      await deleteKontakthistorie(investorId, eintragId);
      toast.success("Eintrag gelöscht.");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const filtered = data.items.filter((e) => {
    if (filter === "manuell") return e.typ === "manuell";
    if (filter === "auto") return e.typ !== "manuell";
    return true;
  });

  return (
    <div data-testid="kontakthistorie-tab" className="space-y-6">
      {/* Add-Form */}
      <section data-testid="kontakthistorie-form" className="border border-navy/15 p-4 space-y-3">
        <div className="pb-eyebrow">Neuer Eintrag</div>
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <label className="pb-label">Datum</label>
            <input type="date" data-testid="kh-datum" className="pb-input--full" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </div>
          <div className="col-span-3">
            <label className="pb-label">Bearbeiter</label>
            <select data-testid="kh-bearbeiter" className="pb-input--full" value={bearbeiter} onChange={(e) => setBearbeiter(e.target.value)}>
              {BEARBEITER_OPTS.map((b) => <option key={b}>{b}</option>)}
            </select>
          </div>
          <div className="col-span-3">
            <label className="pb-label">Kanal</label>
            <select data-testid="kh-kanal" className="pb-input--full" value={kanal} onChange={(e) => setKanal(e.target.value)}>
              {KANAL_OPTS.map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div className="col-span-12">
            <label className="pb-label">Notiz</label>
            <textarea
              data-testid="kh-text"
              rows={2}
              className="pb-input--full"
              placeholder="Was wurde besprochen?"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <div className="col-span-12 flex justify-end">
            <button data-testid="kh-add-btn" onClick={handleAdd} disabled={adding} className="pb-btn-primary gap-2">
              <Plus size={14}/> {adding ? "…" : "Eintrag hinzufügen"}
            </button>
          </div>
        </div>
      </section>

      {/* Filter */}
      <div className="flex items-center gap-2 text-xs font-sans" data-testid="kontakthistorie-filter">
        <FilterIcon size={12} className="text-navy/45"/>
        {["all", "manuell", "auto"].map((f) => (
          <button
            key={f}
            data-testid={`kh-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-2.5 py-1 border ${filter === f ? "bg-navy text-white border-navy" : "border-navy/25 text-navy/70 hover:text-navy"}`}
          >
            {f === "all" ? "Alle" : f === "manuell" ? "Nur manuell" : "Nur Auto"}
          </button>
        ))}
        <span className="ml-auto text-navy/55">{filtered.length} von {data.total}</span>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-navy/45 text-sm font-sans">Lade …</div>
      ) : filtered.length === 0 ? (
        <div className="pb-placeholder">Keine Einträge.</div>
      ) : (
        <ol data-testid="kh-timeline" className="relative border-l-2 border-navy/15 ml-2 space-y-4">
          {filtered.map((e) => {
            const Icon = KANAL_ICONS[e.kanal] || MessageSquare;
            const isAuto = e.typ !== "manuell";
            return (
              <li key={e.id} data-testid={`kh-entry-${e.id}`} className="pl-4 -ml-[7px] relative">
                <span className={`absolute left-0 top-1.5 w-3 h-3 ${isAuto ? "bg-navy/30" : "bg-gold"} rounded-full -translate-x-1/2 border-2 border-white`} />
                <div className="text-xs text-navy/55 font-sans flex items-center gap-2 flex-wrap">
                  <span className="tabular-nums">{fmtDate(e.datum)}</span>
                  <span className="text-[10px] uppercase tracking-widish px-1.5 py-0.5 bg-ledger border border-navy/20 text-navy/75">
                    {e.bearbeiter}
                  </span>
                  <span className="inline-flex items-center gap-1 text-navy/55">
                    <Icon size={11} strokeWidth={1.8}/> {e.kanal}
                  </span>
                  {isAuto && (
                    <span className="text-[10px] uppercase tracking-widish px-1.5 py-0.5 bg-navy/5 text-navy/55 border border-navy/15">
                      {TYP_LABEL[e.typ] || e.typ}
                    </span>
                  )}
                  {!isAuto && (
                    <button
                      onClick={() => handleDelete(e.id)}
                      data-testid={`kh-delete-${e.id}`}
                      className="ml-auto text-red-700/70 hover:text-red-900 inline-flex items-center gap-1 text-[10px] uppercase tracking-widish"
                    >
                      <Trash2 size={10}/> Löschen
                    </button>
                  )}
                </div>
                <div className="text-sm text-navy mt-1 font-sans whitespace-pre-wrap">{e.text}</div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
